import { useState } from "react";
import { ExternalLink, Loader2 } from "lucide-react";
import type { SharepointFolderPreview, SharepointLink } from "@gb/contracts";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/gb/field";
import {
  useLinkSharepointFolder,
  usePreviewSharepointFolder,
  useUnlinkSharepointFolder,
} from "@/features/project/sharepoint-api";
import { formatDayOf } from "@/lib/format";

/**
 * Link the project to a SharePoint folder, or manage the one it has
 * (`plans/sharepoint-sync.md`, questions 1 to 4).
 *
 * A person pastes the folder's address from their browser. The folder is
 * found and its files counted before anything is linked, so the person sees
 * what they are about to bring in. The dialog says who will see the files:
 * everyone in the organisation who uses GroundBrain (decided 2026-10-13).
 *
 * Finding the folder signs the person in with Microsoft, in a popup, so
 * GroundBrain can check the folder opens for them in SharePoint: GroundBrain
 * reaches every site, and a person links only what they can open themselves
 * (2026-10-19). The preview's proof of that check goes with the link.
 */
export function SharepointDialog({
  projectId,
  link,
  onClose,
}: {
  projectId: string;
  link: SharepointLink | null;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<"manage" | "link" | "unlink">(link ? "manage" : "link");

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        {mode === "manage" && link ? (
          <Manage link={link} onRelink={() => setMode("link")} onUnlink={() => setMode("unlink")} onClose={onClose} />
        ) : mode === "unlink" && link ? (
          <Unlink projectId={projectId} link={link} onBack={() => setMode("manage")} onClose={onClose} />
        ) : (
          <LinkFolder projectId={projectId} current={link} onClose={onClose} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function Manage({
  link,
  onRelink,
  onUnlink,
  onClose,
}: {
  link: SharepointLink;
  onRelink: () => void;
  onUnlink: () => void;
  onClose: () => void;
}) {
  return (
    <>
      {/* The kit's header is the title and the close button in one row; a
          description goes in the body (`ui/dialog.tsx`). */}
      <DialogHeader>
        <DialogTitle>SharePoint folder</DialogTitle>
        <DialogCloseButton />
      </DialogHeader>
      <DialogBody>
        <DialogDescription className="mb-4">
          Files added or changed in this folder arrive here within a few minutes. A file deleted there is kept
          here, and no longer used in answers.
        </DialogDescription>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-[0.86rem]">
          <dt className="text-muted">Folder</dt>
          <dd>
            <a
              href={link.folderUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-semibold text-ink underline-offset-2 hover:underline"
            >
              {link.folderName}
              <ExternalLink size={13} aria-hidden />
              <span className="sr-only">(opens SharePoint)</span>
            </a>
          </dd>
          <dt className="text-muted">Where</dt>
          <dd className="text-ink-slate">{link.location}</dd>
          <dt className="text-muted">Linked</dt>
          <dd className="text-ink-slate">
            {formatDayOf(link.linkedAt)}
            {link.linkedBy ? `, by ${link.linkedBy}` : ""}
          </dd>
          <dt className="text-muted">Files here</dt>
          <dd className="text-ink-slate">{link.files}</dd>
        </dl>
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={onUnlink}>
          Stop syncing
        </Button>
        <Button variant="secondary" onClick={onRelink}>
          Link another folder
        </Button>
        <Button className="hover:bg-brand-deep" onClick={onClose}>
          Done
        </Button>
      </DialogFooter>
    </>
  );
}

function Unlink({
  projectId,
  link,
  onBack,
  onClose,
}: {
  projectId: string;
  link: SharepointLink;
  onBack: () => void;
  onClose: () => void;
}) {
  const unlink = useUnlinkSharepointFolder(projectId);
  return (
    <>
      <DialogHeader>
        <DialogTitle>Stop syncing “{link.folderName}”?</DialogTitle>
        <DialogCloseButton />
      </DialogHeader>
      <DialogBody>
        <DialogDescription>
          The files already here stay in the project. Changes made in SharePoint after this won’t arrive.
        </DialogDescription>
        {unlink.error && (
          <p role="alert" className="mt-3 text-[0.85rem] font-semibold text-danger">
            {unlink.error.message}
          </p>
        )}
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={onBack} disabled={unlink.isPending}>
          Back
        </Button>
        <Button
          className="hover:bg-brand-deep"
          disabled={unlink.isPending}
          onClick={() => void unlink.mutateAsync().then(onClose)}
        >
          {unlink.isPending && <Loader2 size={16} className="animate-spin" />}
          Stop syncing
        </Button>
      </DialogFooter>
    </>
  );
}

function LinkFolder({
  projectId,
  current,
  onClose,
}: {
  projectId: string;
  current: SharepointLink | null;
  onClose: () => void;
}) {
  const [url, setUrl] = useState("");
  const [found, setFound] = useState<SharepointFolderPreview | null>(null);
  const preview = usePreviewSharepointFolder(projectId);
  const link = useLinkSharepointFolder(projectId);
  const error = preview.error?.message ?? link.error?.message ?? null;
  const busy = preview.isPending || link.isPending;

  const find = async (): Promise<void> => {
    setFound(null);
    link.reset();
    setFound(await preview.mutateAsync(url.trim()).catch(() => null));
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Link a SharePoint folder</DialogTitle>
        <DialogCloseButton />
      </DialogHeader>
      <DialogBody>
        <div className="flex flex-col gap-[18px]">
          <DialogDescription>
            Every file in the folder and its subfolders comes into this project, and changes keep arriving.
            Everyone in your organisation who uses GroundBrain will see them.
          </DialogDescription>
          <Field
            label="Folder address"
            hint="Open the folder in SharePoint and copy the address from your browser. Microsoft then asks you to sign in, to check you can open it."
          >
            {(props) => (
              <Input
                {...props}
                value={url}
                onChange={(event) => {
                  setUrl(event.target.value);
                  setFound(null);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && url.trim() && !busy) void find();
                }}
                placeholder="https://…sharepoint.com/sites/…"
                inputMode="url"
                autoComplete="off"
              />
            )}
          </Field>
          {found && (
            <div className="rounded-card-lg border-[1.5px] border-border bg-subtle/60 px-4 py-3 text-[0.86rem]">
              <p className="font-semibold text-ink">{found.folderName}</p>
              <p className="text-ink-slate">{found.location}</p>
              <p className="mt-1 text-ink-slate">
                {found.complete
                  ? found.files === 1
                    ? "1 file, with its subfolders."
                    : `${found.files} files, with its subfolders.`
                  : `At least ${found.files} files, with its subfolders.`}
              </p>
              {current && (
                <p className="mt-1 text-muted">The files of “{current.folderName}” stay in the project.</p>
              )}
            </div>
          )}
          {error && (
            <p role="alert" className="text-[0.85rem] font-semibold text-danger">
              {error}
            </p>
          )}
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="secondary" onClick={onClose} disabled={link.isPending}>
          Cancel
        </Button>
        {found ? (
          <Button
            className="hover:bg-brand-deep"
            disabled={busy}
            onClick={() =>
              void link
                .mutateAsync({ url: url.trim(), proof: found.proof })
                .then(onClose)
                .catch(() => undefined)
            }
          >
            {link.isPending && <Loader2 size={16} className="animate-spin" />}
            Link this folder
          </Button>
        ) : (
          <Button className="hover:bg-brand-deep" disabled={busy || !url.trim()} onClick={() => void find()}>
            {preview.isPending && <Loader2 size={16} className="animate-spin" />}
            Find the folder
          </Button>
        )}
      </DialogFooter>
    </>
  );
}
